import { fetchAuthSession } from 'aws-amplify/auth';

const fetchApi = async ({
  endpoint,
  method = 'GET',
  params = {},
  body = undefined,
  httpResponse = false,
  validateStatusFn = null, // (status) => bool
  timeoutMs = null // give up after this many ms (null = wait indefinitely, the default)
}) => {
  // Opt-in per call: some endpoints (jobs sync, optimizer runs) are legitimately slow
  // and must keep their unbounded wait.
  const controller = timeoutMs ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  try {
    const session = await fetchAuthSession();
    const searchParams = new URLSearchParams(params);
    const url = `${process.env.REACT_APP_FETCH_API_URL}/${endpoint}${searchParams ? `?${searchParams}` : ''}`;

    const response = await fetch(
      url,
      {
        method,
        body,
        headers: {
          'X-API-KEY': session.tokens?.idToken?.payload['custom:FETCH_API_TOKEN'],
          'Content-Type': 'application/json'
        },
        signal: controller?.signal
      }
    );

    // Check if response is OK (status 200-299)
    if (!response.ok && validateStatusFn == null) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Request failed with status ${response.status}: ${errorText}`);
    }

    if (validateStatusFn != null) {
      if (validateStatusFn(response.status)) {
        if (httpResponse) {
          return response;
        }

        // Check if response has JSON content before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            return await response.json();
          } catch (jsonError) {
            console.error('Failed to parse JSON response:', jsonError);
            throw new Error('Invalid JSON response from server');
          }
        }

        // Empty response or non-JSON response
        return null;
      }

      throw new Error(`Request failed with status code ${response.status}`);
    }

    if (httpResponse) {
      return response;
    }

    // Check if response has JSON content before parsing
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        // CRITICAL: Throw error instead of returning empty object
        // Returning {} causes calling code to treat it as valid empty data
        throw new Error(`Invalid JSON response from ${endpoint}: ${jsonError.message}`);
      }
    }

    // Empty response or non-JSON response - return null
    return null;
  } catch (error) {
    // Log error for debugging
    console.error('fetchApi error:', {
      endpoint,
      method,
      error: error.message
    });

    // Re-throw with more context
    if (error.name === 'AbortError') {
      // The server may still have applied the change - say so rather than implying it failed.
      throw new Error(`The server did not respond within ${Math.round(timeoutMs / 1000)}s. The change may still have gone through - refresh before trying again.`);
    }

    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error: Unable to reach the server. Please check your internet connection.');
    }

    throw error;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

export default fetchApi;
